/**
 * Client trust banner shown after the dashboard unlocks but before onboarding
 * verification is approved. It keeps early data visible without implying that
 * it is safe to use yet.
 */

import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "./api-context.js";

type Stage = "not_started" | "checking" | "needs_answers" | "team_review" | "verified";

/** The guided setup pages already explain verification in the right place.
 * Showing this global banner there duplicates the message and can expose an
 * earlier test run while the product review is still open. */
export function shouldShowVerificationReadinessBanner(pathname: string): boolean {
  const isAtOrUnder = (root: string): boolean => pathname === root || pathname.startsWith(`${root}/`);
  return !isAtOrUnder("/welcome")
    && !isAtOrUnder("/onboarding")
    && !isAtOrUnder("/costs/overhead");
}

export function VerificationReadinessBanner(): React.ReactElement | null {
  const api = useApiClient();
  const location = useLocation();
  const q = useQuery({
    queryKey: ["onboarding", "journey"],
    queryFn: () => api.get<{ verification: { stage: Stage; checksPassed: number } }>("/onboarding/journey"),
    staleTime: 30_000,
    refetchInterval: (query) => ["checking", "team_review"].includes(query.state.data?.verification.stage ?? "") ? 30_000 : false,
  });
  const verification = q.data?.verification;
  if (!shouldShowVerificationReadinessBanner(location.pathname) || !verification || verification.stage === "verified") return null;

  const copy = (() => {
    if (verification.stage === "checking") return { title: "Your numbers are being checked", body: "You can look around, but do not make business decisions from these numbers yet.", cta: "See check progress" };
    if (verification.stage === "needs_answers") return { title: "Your numbers are not verified yet", body: "We need a little more information before the dashboard is safe to use.", cta: "See what is needed" };
    if (verification.stage === "team_review") return { title: "Automatic checks finished. Our team is reviewing them.", body: "You can look around, but do not make business decisions from these numbers until the verified check appears.", cta: "See check progress" };
    return { title: "Your numbers are not verified yet", body: "Finish the accuracy check before you use this dashboard to make decisions.", cta: "Start verification" };
  })();

  return (
    <Link
      to="/welcome?step=7"
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 14,
        flexWrap: "wrap",
        marginBottom: 12,
        padding: "12px 14px",
        border: "2px solid #dc2626",
        borderRadius: 10,
        background: "color-mix(in srgb, #dc2626 7%, var(--surface))",
        color: "var(--text)",
        textDecoration: "none",
      }}
    >
      <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {["checking", "team_review"].includes(verification.stage) && <span className="dd-spinner" style={{ width: 17, height: 17, flexShrink: 0 }} />}
        <span><b style={{ color: "#dc2626", fontSize: 14 }}>{copy.title}</b><span style={{ display: "block", color: "var(--body)", fontSize: 12.5, marginTop: 2 }}>{copy.body}</span></span>
      </span>
      <span style={{ color: "#dc2626", fontWeight: 750, fontSize: 12.5 }}>{copy.cta} →</span>
    </Link>
  );
}
